import type { Lead, LeadList, Membership } from './types';

/**
 * What the Hub already knows about an unclassified lead.
 *
 * 569 of 688 leads have no Lead Source. Classifying them one at a time is a
 * project nobody finishes; classifying them automatically is guessing, which
 * Kyu ruled out over the LinkedIn origins and was right to. The middle path is
 * this file: group the queue by evidence that already exists, show the
 * evidence, and let a person confirm a whole group at once.
 *
 * A decision covering 212 leads is a decision somebody can weigh. 212
 * decisions is a decision nobody makes.
 *
 * ── Measured before this was written ───────────────────────────────────────
 *
 *   on an outbound list                    212   (165 of them with sends)
 *   on a non-outbound list only            164   (all CG-Nurture campaigns)
 *   no list and no activity                193
 *                                          ---
 *                                          569
 *
 * The last 193 looked at first like a permanent shrug. They are not. Grouped
 * by when they were created and by whom:
 *
 *   2026-08-14  karl.fredeluces   125
 *   2026-07-29  kyu.renegado       63
 *   2026-07-24  kyu.renegado        3
 *   2026-08-17  karl.fredeluces     1
 *   2026-08-17  kyu.renegado        1
 *
 * Two imports account for 188 of the 193. That is the difference between "we
 * do not know where these came from" and "that is the batch Karl loaded on the
 * 14th" — the second is a question a person can actually answer.
 *
 * ── Two rules that were proposed and are NOT here ──────────────────────────
 *
 * The cleanup plan proposed four evidence rules. Two classify exactly zero
 * leads on the real data, so they are not built:
 *
 *   "Lead Origin is an inbound-only value" — all 569 are missing Lead Origin.
 *   "replied before we ever sent"          — not one of the 569 has a reply.
 *
 * Written down rather than quietly dropped, so nobody re-proposes them from
 * the plan without checking the data again.
 *
 * ── Why no activity query ──────────────────────────────────────────────────
 *
 * The grouping needs none. On the measured data every lead with a recorded
 * send is also on a list, so membership alone separates the groups, and
 * `touch_count` — already on the lead row — supplies the "with sends" detail.
 */

export type EvidenceKey = string;

export interface EvidenceGroup {
  key: EvidenceKey;
  label: string;
  /** The claim somebody is confirming. Not a count — a sentence. */
  claim: string;
  /** The Lead Source this evidence supports, or null when it supports none. */
  proposes: 'inbound' | 'outbound' | null;
  leads: number;
  /** Of those, how many carry a recorded touch. Detail, not its own group. */
  withSends: number;
}

export const OUTBOUND_LIST = 'outbound-list';
export const OTHER_LIST = 'other-list';
export const IMPORT_PREFIX = 'import:';
export const ODDS_AND_ENDS = 'odds-and-ends';

const OUTBOUND_TYPE = 'Outbound';

/**
 * An import batch earns its own chip at ten leads.
 *
 * Not a tuned number, a threshold with a reason: a chip exists so somebody can
 * act on a group as a group. Below about ten, opening the leads and reading
 * them is faster than reasoning about the batch — so the small ones collapse
 * into one remainder rather than turning the bar into a list of ones.
 *
 * On the live data this draws the line in the obvious place: two chips of 125
 * and 63, and five leftovers.
 */
const BATCH_CHIP_MINIMUM = 10;

function importKey(lead: Lead): string {
  // Day plus creator. Either alone is ambiguous — two people importing on one
  // day are two batches, and one person importing twice in a week is two too.
  const day = lead.created_at.slice(0, 10);
  return `${IMPORT_PREFIX}${day}:${lead.created_by ?? 'unknown'}`;
}

function dayLabel(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/**
 * Which evidence group each lead falls in.
 *
 * Every lead passed in gets an entry, so a lead missing from a populated map
 * never happens and callers do not have to decide what absence means.
 */
export function evidenceByLead(
  leads: Lead[],
  memberships: Membership[],
  lists: LeadList[],
): Map<string, EvidenceKey> {
  const outboundListIds = new Set(
    lists.filter((list) => list.type === OUTBOUND_TYPE).map((list) => list.id),
  );

  const onOutbound = new Set<string>();
  const onAnyList = new Set<string>();
  for (const membership of memberships) {
    onAnyList.add(membership.lead_id);
    if (outboundListIds.has(membership.list_id)) {
      onOutbound.add(membership.lead_id);
    }
  }

  // Batch sizes first, so a lead from a small import lands in the remainder
  // rather than in a chip of its own that nobody would ever click.
  const batchSizes = new Map<string, number>();
  for (const lead of leads) {
    if (onAnyList.has(lead.id)) {
      continue;
    }
    const key = importKey(lead);
    batchSizes.set(key, (batchSizes.get(key) ?? 0) + 1);
  }

  const byLead = new Map<string, EvidenceKey>();
  for (const lead of leads) {
    if (onOutbound.has(lead.id)) {
      // Outbound wins over "on some other list": a lead on both a Batch list
      // and a nurture list was still emailed on an outbound cadence, and that
      // is the stronger fact. 25 leads sit in exactly that position.
      byLead.set(lead.id, OUTBOUND_LIST);
      continue;
    }
    if (onAnyList.has(lead.id)) {
      byLead.set(lead.id, OTHER_LIST);
      continue;
    }
    const key = importKey(lead);
    byLead.set(lead.id, (batchSizes.get(key) ?? 0) >= BATCH_CHIP_MINIMUM ? key : ODDS_AND_ENDS);
  }
  return byLead;
}

/**
 * The groups present in this set of leads, with the evidence behind each.
 *
 * Built from the data rather than declared, because the import batches are not
 * knowable in advance — and a hardcoded list of them would be stale the next
 * time somebody imports a spreadsheet.
 *
 * Ordered by how much the evidence proves, then by size. The group that can be
 * confirmed most confidently is the one somebody should clear first.
 */
export function evidenceGroups(
  leads: Lead[],
  memberships: Membership[],
  lists: LeadList[],
  peopleById: Map<string, string> = new Map(),
): EvidenceGroup[] {
  const byLead = evidenceByLead(leads, memberships, lists);
  const listNameById = new Map(lists.map((list) => [list.id, list.name]));
  const listsByLead = new Map<string, string[]>();
  for (const membership of memberships) {
    listsByLead.set(membership.lead_id, [
      ...(listsByLead.get(membership.lead_id) ?? []),
      membership.list_id,
    ]);
  }

  const buckets = new Map<EvidenceKey, Lead[]>();
  for (const lead of leads) {
    const key = byLead.get(lead.id) ?? ODDS_AND_ENDS;
    buckets.set(key, [...(buckets.get(key) ?? []), lead]);
  }

  const named = (key: EvidenceKey, mine: Lead[]): EvidenceGroup => {
    const withSends = mine.filter((lead) => lead.touch_count > 0).length;
    const base = { key, leads: mine.length, withSends };

    if (key === OUTBOUND_LIST) {
      return {
        ...base,
        label: 'On an outbound list',
        claim: 'We put these on an outbound cadence and emailed them.',
        proposes: 'outbound',
      };
    }

    if (key === OTHER_LIST) {
      // Proposes nothing, deliberately. On the live data `CG-Nurture-Class
      // B.4` and `B.6` are typed Outbound while `B.1`, `B.2`, `B.3`, `B.5`,
      // `B.7`, `A.1` and `A.2` are typed Nurture — one campaign family split
      // across two types, which reads as data-entry drift rather than a real
      // distinction. The type is not strong enough to classify on.
      //
      // So the group names its lists instead. Somebody who ran those campaigns
      // recognises them at a glance and confirms on what they know, rather
      // than on what a rule assumed.
      const names = new Set<string>();
      for (const lead of mine) {
        for (const listId of listsByLead.get(lead.id) ?? []) {
          const name = listNameById.get(listId);
          if (name !== undefined && name !== '') {
            names.add(name);
          }
        }
      }
      const sorted = [...names].sort();
      const shown = sorted.slice(0, 4).join(', ');
      return {
        ...base,
        label: 'On a nurture list only',
        claim:
          sorted.length === 0
            ? 'On a list that is not marked Outbound.'
            : `On ${sorted.length === 1 ? 'this list' : 'these lists'}: ${shown}${
                sorted.length > 4 ? `, and ${String(sorted.length - 4)} more` : ''
              }.`,
        proposes: null,
      };
    }

    if (key.startsWith(IMPORT_PREFIX)) {
      const [, day = '', actor = ''] = key.split(':');
      const who = peopleById.get(actor);
      return {
        ...base,
        label: `Imported ${dayLabel(day)}`,
        claim:
          who === undefined
            ? `Added on ${dayLabel(day)}, on no list, never touched.`
            : `Added on ${dayLabel(day)} by ${who}, on no list, never touched.`,
        proposes: null,
      };
    }

    return {
      ...base,
      label: 'Everything else',
      claim: 'On no list, never touched, and not part of a batch import.',
      proposes: null,
    };
  };

  const rank = (key: EvidenceKey): number =>
    key === OUTBOUND_LIST ? 0 : key === OTHER_LIST ? 1 : key === ODDS_AND_ENDS ? 3 : 2;

  return [...buckets.entries()]
    .map(([key, mine]) => named(key, mine))
    .sort((a, b) => rank(a.key) - rank(b.key) || b.leads - a.leads)
    .filter((group) => group.leads > 0);
}
